import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Building2, Mail, Image, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import Input from "@/components/ui/Input";
import Button from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import TesterSettings from "@/pages/dashboard/Settings";
import { useApiData } from "@/hooks/useApiData";
import { useAuth } from "@/context/AuthContext";
import { validateCompanyName, validateEmail } from "@/lib/validation";
import { getCompanySettings, updateCompanySettings } from "@/api/company";
import { COUNTRIES } from "@/data/countries";

const DEMO = { id: "demo", name: "Acme Inc.", billing_email: "billing@acme.com", logo_url: "", model_preset: "balanced", country: "" };

// Reuses the shared account Settings toast look.
function Toast({ type, message, onClose }) {
  return (
    <AnimatePresence>
      {message && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          className={cn(
            "mb-5 flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm",
            type === "error"
              ? "border-rose-200 bg-rose-50/70 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-200"
              : "border-emerald-200 bg-emerald-50/70 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-200"
          )}
        >
          {type === "error" ? <AlertCircle size={15} className="shrink-0" /> : <CheckCircle2 size={15} className="shrink-0" />}
          <span className="flex-1">{message}</span>
          <button type="button" onClick={onClose} className="text-xs font-medium opacity-60 hover:opacity-100">✕</button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// The company-specific panel, styled to match the tester Settings tab cards.
function CompanyTab() {
  const { data, demo } = useApiData(getCompanySettings, DEMO);
  const { refreshUser } = useAuth();
  const [form, setForm] = useState(DEMO);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => { if (data) setForm({ ...DEMO, ...data }); }, [data]);

  async function save(e) {
    e.preventDefault();
    // Both checks mirror the backend's own (#100/#101/#102/#103) — this form
    // had none at all before, client or server, so a blank name, "1234@#$%",
    // or "sai@a" all saved without complaint.
    const next = {};
    const nameError = validateCompanyName(form.name);
    if (nameError) next.name = nameError;
    const emailError = validateEmail(form.billing_email);
    if (emailError) next.billing_email = emailError;
    setErrors(next);
    if (Object.keys(next).length) return;
    setBusy(true);
    try {
      await updateCompanySettings({
        name: form.name.trim(),
        billing_email: form.billing_email.trim(),
        logo_url: form.logo_url,
        model_preset: form.model_preset,
        country: form.country,
      });
      // The sidebar's "Company" badge (and now its logo, #104) reads
      // user.company_name/company_logo_url from AuthContext, not from this
      // form — without this, the save genuinely worked but looked like it
      // hadn't, since nothing else on screen ever changed (#99).
      await refreshUser();
      setToast({ type: "success", message: "Company settings saved." });
    } catch (err) {
      setToast({ type: "error", message: (err && err.message) || "Failed to save settings." });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={save} className="space-y-5">
      <Toast {...(toast || {})} onClose={() => setToast(null)} />

      {demo && (
        <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-2.5 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
          <AlertCircle size={15} className="shrink-0" /> Showing demo data — backend not reachable.
        </div>
      )}

      <div className="card p-6">
        <h3 className="text-base font-semibold text-slate-800 dark:text-white">Company profile</h3>
        <p className="mt-0.5 text-sm text-slate-400">How your company appears, and where billing notices go.</p>
        <div className="mt-5 space-y-5">
          <Input label="Company name" icon={Building2} value={form.name || ""} error={errors.name}
                 onChange={(e) => { setForm((f) => ({ ...f, name: e.target.value })); setErrors((er) => ({ ...er, name: undefined })); }} />
          <Input label="Billing email" icon={Mail} type="email" value={form.billing_email || ""} error={errors.billing_email}
                 onChange={(e) => { setForm((f) => ({ ...f, billing_email: e.target.value })); setErrors((er) => ({ ...er, billing_email: undefined })); }} />
          <Input label="Logo URL (optional)" icon={Image} placeholder="https://…/logo.png" value={form.logo_url || ""} onChange={(e) => setForm((f) => ({ ...f, logo_url: e.target.value }))} />
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Country</span>
            <select value={form.country || ""} onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
              className="h-11 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm dark:border-white/10 dark:bg-white/5 dark:text-white">
              {COUNTRIES.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
            </select>
            <span className="mt-1 block text-xs text-slate-400">Used for country-wise plan pricing at checkout.</span>
          </label>
        </div>
      </div>

      <Button type="submit" disabled={busy}>
        {busy ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Save changes
      </Button>
    </form>
  );
}

export default function CompanySettings() {
  return (
    <TesterSettings
      subtitle="Manage your company, profile, security and preferences."
      initialTab="company"
      extraTabs={[
        { id: "company", label: "Company", icon: Building2, hint: "Company profile", element: <CompanyTab /> },
      ]}
    />
  );
}
